import {useCallback} from 'react';
import {useInView} from 'react-intersection-observer';
import {shallowEqual} from 'react-redux';

import type * as Redux from '../../types/redux';
import {type RootState} from '../../types/redux';
import {useDispatch, useSelector} from '../hooks';
import {setHighlight, setHighlights, setPanelSelection} from './actions';
import {EMPTY_STATE} from './reducer';
import {type InteractState} from './types';

// Performant way to get notified when interactState relevant to your component
// changes.
// Pass a mapFn and get back a tuple [domRef, value]. Assign the domRef to the
// target element in your component.
// Value only updates while domRef is onscreen, and the component only
// re-renders when the result of your mapFn changes (compared with shallowEqual).
//
// mapFn MUST run inside the selector: the reducer returns a new interactState
// object on every dispatch, so selecting the whole object and mapping the
// result afterwards re-renders every subscriber on every dispatch (the
// line-plot hover render-thrash). Applying mapFn inside the selector lets
// react-redux bail out when the mapped slice is unchanged.
export function useInteractStateWhenOnScreen<R>(
  mapFn: (interactState: InteractState) => R
) {
  const [domRef, onScreen] = useInView();
  const value = useSelector(
    (state: RootState) => mapFn(onScreen ? state.interactState : EMPTY_STATE),
    shallowEqual
  );
  return [domRef, value] as const;
}

export function useInteractStateAction<ArgType extends any[]>(
  fn: (...fnArgs: ArgType) => Redux.DispatchableAction
) {
  const dispatch = useDispatch();
  return useCallback(
    (...args: ArgType) => dispatch(fn(...args)),
    [dispatch, fn]
  );
}

export function useSetHighlight() {
  return useInteractStateAction(setHighlight);
}

export function useSetHighlights() {
  return useInteractStateAction(setHighlights);
}

export function useSetPanelSelection() {
  return useInteractStateAction(setPanelSelection);
}
